import { useEffect, useState } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { useTonWallet } from '@tonconnect/ui-react'
import type { AppContext } from '../components/layout/AppLayout'
import type { Model, ModelCategory } from '../lib/api'
import { api } from '../lib/api'
import { Input, CategoryChip, SortDropdown, StickyHeader, ProviderBadge, CocoonBadge } from '../components/ui'
import { Search, ArrowLeftRight } from 'lucide-react'
import { STATIC_MODELS } from '../lib/models-static'
import { haptics } from '../lib/haptics'
import ModelDetailSheet from '../components/ModelDetailSheet'
import PrivacyInfoSheet from '../components/PrivacyInfoSheet'

const CATEGORY_LABELS: Record<ModelCategory, string> = {
  secure: '🥚 Cocoon',
  flagship: '🏆 Flagship',
  everyday: '🤖 General',
  coding: '💻 Coding',
  research: '🔬 Research',
  fast: '⚡ Fast',
}

const CATEGORY_ORDER: ModelCategory[] = ['secure', 'flagship', 'everyday', 'coding', 'research', 'fast']

const SORT_OPTIONS = [
  { label: 'Popular', value: 'popular' },
  { label: 'Price ↑', value: 'price_asc' },
  { label: 'Price ↓', value: 'price_desc' },
  { label: 'Context ↓', value: 'ctx_desc' },
]

const POPULAR_IDS = [
  'openai/gpt-5',
  'anthropic/claude-sonnet-4.6',
  'google/gemini-2.5-flash',
  'deepseek/deepseek-v3.2',
  'openai/o4-mini',
]

function formatPrice(usdPerToken: number): string {
  const perM = usdPerToken * 1_000_000
  if (perM < 0.01) return `$${perM.toFixed(3)}/M`
  if (perM < 1) return `$${perM.toFixed(2)}/M`
  return `$${perM.toFixed(1)}/M`
}

function formatCtx(ctx: number): string {
  if (ctx >= 1_000_000) return `${(ctx / 1_000_000).toFixed(0)}M`
  return `${(ctx / 1000).toFixed(0)}K`
}

export default function ModelsPage() {
  const { apiKey, balance } = useOutletContext<AppContext>()
  const wallet = useTonWallet()
  const address = wallet?.account.address
  // Find the name of the active key by matching secrets from localStorage
  const activeKeyName = (() => {
    if (!address || !apiKey || !balance?.keys) return undefined
    for (const k of balance.keys) {
      const secret = localStorage.getItem(`imago:key:${address}:${k.id}`)
      if (secret === apiKey) return k.name
    }
    // Legacy: active key stored without per-ID — return name of first key
    return balance.keys[0]?.name
  })()
  const navigate = useNavigate()
  const [models, setModels] = useState<Model[]>(STATIC_MODELS)
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState<ModelCategory | null>(null)
  const [sort, setSort] = useState('popular')
  const [selectedModel, setSelectedModel] = useState<Model | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [privacyOpen, setPrivacyOpen] = useState(false)

  useEffect(() => {
    api.listModels()
      .then(setModels)
      .catch(() => { /* keep static data */ })
  }, [])

  const popularModels = POPULAR_IDS
    .map((id) => models.find((m) => m.id === id))
    .filter((m): m is Model => m !== undefined && m.status !== 'maintenance')

  const showPopular = !search && !activeCategory && popularModels.length >= 3

  // Filter
  let filtered = models
  if (search) {
    const q = search.toLowerCase()
    filtered = filtered.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.id.toLowerCase().includes(q) ||
        m.description?.toLowerCase().includes(q),
    )
  }
  if (activeCategory) {
    filtered = filtered.filter((m) => m.category === activeCategory)
  }

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    switch (sort) {
      case 'price_asc':
        return a.pricing.prompt_usd_per_token - b.pricing.prompt_usd_per_token
      case 'price_desc':
        return b.pricing.prompt_usd_per_token - a.pricing.prompt_usd_per_token
      case 'ctx_desc':
        return b.context_length - a.context_length
      case 'popular': {
        const rankA = POPULAR_IDS.indexOf(a.id)
        const rankB = POPULAR_IDS.indexOf(b.id)
        const ra = rankA === -1 ? POPULAR_IDS.length : rankA
        const rb = rankB === -1 ? POPULAR_IDS.length : rankB
        return ra - rb
      }
      default:
        return 0
    }
  })

  // Group by category
  const grouped = new Map<ModelCategory, Model[]>()
  for (const cat of CATEGORY_ORDER) grouped.set(cat, [])
  for (const m of sorted) {
    grouped.get(m.category ?? 'everyday')?.push(m)
  }

  const openDetail = (model: Model) => {
    haptics.impact('light')
    setSelectedModel(model)
    setDetailOpen(true)
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Search + Compare — no redundant page title, all controls in one row */}
      <div className="shrink-0 flex items-center gap-sp-2 px-sp-4 pt-sp-3 pb-sp-2">
        <div className="flex-1">
          <Input
            leftIcon={<Search size={16} />}
            placeholder="Model or task..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          onClick={() => { haptics.impact('light'); navigate('/compare') }}
          className="w-11 h-11 flex items-center justify-center rounded-xl active:opacity-70 transition-opacity shrink-0"
          style={{
            backgroundColor: 'var(--tg-theme-secondary-bg-color)',
            color: 'var(--tg-theme-accent-text-color)',
          }}
        >
          <ArrowLeftRight size={18} />
        </button>
      </div>

      {/* Chips + Sort — same visual row, equal padding top/bottom */}
      <div className="shrink-0 flex items-center gap-sp-2 py-sp-2">
        {/* Chips scroll — wrapped in relative for the right fade mask */}
        <div className="relative flex-1 min-w-0 overflow-hidden">
          <div className="flex gap-sp-2 scroll-x pl-sp-4">
            <CategoryChip
              label="All"
              active={activeCategory === null}
              onClick={() => setActiveCategory(null)}
            />
            {CATEGORY_ORDER.map((cat) => (
              <CategoryChip
                key={cat}
                label={CATEGORY_LABELS[cat]}
                active={activeCategory === cat}
                onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                variant={cat === 'secure' ? 'secure' : 'default'}
              />
            ))}
            {/* Trailing spacer — fixes overflow-x right-padding bug */}
            <div className="shrink-0 w-6" />
          </div>
          {/* Right fade — softens chip cutoff before the Sort button */}
          <div
            className="absolute right-0 top-0 bottom-0 w-10 pointer-events-none"
            style={{
              background: 'linear-gradient(to right, transparent, var(--tg-theme-bg-color))',
            }}
          />
        </div>

        {/* Sort — fixed to the right, outside scroll */}
        <div className="shrink-0 pr-sp-3">
          <SortDropdown options={SORT_OPTIONS} selected={sort} onChange={setSort} />
        </div>
      </div>

      {/* Model list */}
      <div className="flex-1 overflow-y-auto scroll-area">
        {/* Popular row */}
        {showPopular && (
          <div>
            <StickyHeader>🔥 Popular Now</StickyHeader>
            <div className="flex gap-sp-2 pt-sp-3 pb-sp-3 scroll-x pl-sp-4">
              {popularModels.map((m) => (
                <button
                  key={m.id}
                  onClick={() => openDetail(m)}
                  className="flex flex-col items-start gap-1 rounded-[14px] p-sp-3 shrink-0 w-[136px] text-left active:opacity-70 transition-opacity overflow-hidden"
                  style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
                >
                  <ProviderBadge modelId={m.id} size="sm" />
                  <span
                    className="text-[15px] font-semibold truncate w-full mt-1"
                    style={{ color: 'var(--tg-theme-text-color)' }}
                  >
                    {m.name}
                  </span>
                  <span
                    className="text-[12px] tabular-nums"
                    style={{ color: 'var(--tg-theme-hint-color)' }}
                  >
                    {formatPrice(m.pricing.prompt_usd_per_token)} in
                  </span>
                </button>
              ))}
              {/* Trailing spacer — padding-right ignored in overflow-x flex */}
              <div style={{ flexShrink: 0, width: '16px' }} />
            </div>
          </div>
        )}

        {activeCategory || search ? (
          sorted.map((m) => (
            <ModelRow key={m.id} model={m} onClick={() => openDetail(m)} onPrivacyOpen={() => setPrivacyOpen(true)} />
          ))
        ) : (
          CATEGORY_ORDER.map((cat) => {
            const group = grouped.get(cat) ?? []
            if (group.length === 0) return null
            return (
              <div key={cat}>
                <StickyHeader>{CATEGORY_LABELS[cat]}</StickyHeader>
                {group.map((m) => (
                  <ModelRow key={m.id} model={m} onClick={() => openDetail(m)} onPrivacyOpen={() => setPrivacyOpen(true)} />
                ))}
              </div>
            )
          })
        )}

        {sorted.length === 0 && (
          <div className="flex flex-col items-center justify-center flex-1 gap-sp-3 px-sp-8 py-sp-12">
            <p
              className="text-[15px] font-medium text-center"
              style={{ color: 'var(--tg-theme-text-color)' }}
            >
              No models found
            </p>
            <p
              className="text-[13px] text-center"
              style={{ color: 'var(--tg-theme-hint-color)' }}
            >
              Try a different search or category
            </p>
          </div>
        )}
      </div>

      {/* Model detail sheet */}
      <ModelDetailSheet
        model={selectedModel}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        apiKey={apiKey}
        activeKeyName={activeKeyName}
      />

      {/* Privacy info sheet — for TEE badge taps */}
      <PrivacyInfoSheet
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
      />
    </div>
  )
}

function ModelRow({ model, onClick, onPrivacyOpen }: { model: Model; onClick: () => void; onPrivacyOpen?: () => void }) {
  const isSecure = model.id.startsWith('cocoon/')
  const isMaintenance = model.status === 'maintenance'
  return (
    <button
      onClick={isMaintenance ? undefined : onClick}
      disabled={isMaintenance}
      className={`w-full flex items-center px-sp-4 py-sp-3 transition-opacity text-left ${
        isMaintenance ? 'opacity-50' : 'active:opacity-70'
      }`}
      style={{ borderBottom: '1px solid var(--tg-theme-secondary-bg-color)' }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-sp-2 flex-wrap">
          <span
            className="text-[15px] font-medium"
            style={{ color: 'var(--tg-theme-text-color)' }}
          >
            {model.name}
          </span>
          {isSecure
            ? <CocoonBadge onClick={isMaintenance ? undefined : onPrivacyOpen} />
            : <ProviderBadge modelId={model.id} size="sm" />
          }
          <span
            className="text-[11px] shrink-0 tabular-nums"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            {formatCtx(model.context_length)}
          </span>
        </div>
        {/* Model ID — key artifact for developers */}
        <p
          className="text-[11px] mt-0.5 font-mono truncate"
          style={{ color: 'var(--tg-theme-hint-color)' }}
        >
          {model.id}
        </p>
        {model.description && (
          <p
            className="text-[12px] mt-0.5 truncate"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            {model.description}
          </p>
        )}
      </div>
      {isMaintenance ? (
        <span
          className="text-[11px] font-semibold shrink-0 ml-sp-3 px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: 'rgba(255, 159, 10, 0.15)',
            color: 'rgb(255, 159, 10)',
          }}
        >
          Maintenance
        </span>
      ) : (
        <span
          className="text-[13px] font-mono shrink-0 ml-sp-3 tabular-nums"
          style={{ color: 'var(--tg-theme-hint-color)' }}
        >
          {formatPrice(model.pricing.prompt_usd_per_token)}
        </span>
      )}
    </button>
  )
}
